import { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { useDebouncedCallback } from 'use-debounce';
import { CategoryFilter, CreateCategoryRequest, QueryParams, TableEnum, UseAddCategoryOperationsProps } from 'types';
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useGetByIdCategoryQuery,
  useGetAllAttributesQuery,
  useCheckUniqueMutation,
} from 'services';
import { CategoryFormValues } from 'models/Category';
import { showAlertNotification } from 'components/common';

export const useAddCategoryOperations = ({
  editMode = false,
  editingCategory = null,
  onGenerate,
  onSuccess,
  isOpen,
  isCopyMode = false,
  setExternalRowValues,
  setExternalConfirmedRows,
  formInstance,
}: UseAddCategoryOperationsProps) => {
  const [localForm] = Form.useForm();
  const form = formInstance || localForm;
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [checkUnique, { isLoading: isCheckingUnique }] = useCheckUniqueMutation();
  const [filter, setFilter] = useState<CategoryFilter>({});
  const [isCodeGenerated, setIsCodeGenerated] = useState<boolean>(false);
  const [shouldLoadAttributes, setShouldLoadAttributes] = useState<boolean>(false);
  const [genCode, setGenCode] = useState<string>(form.getFieldValue('code') || '');
  const [attributeQuery, setAttributeQuery] = useState<QueryParams>({
    size: 20,
    page: 1,
  } as unknown as QueryParams);
  const {
    data: categoryDetailData,
    isLoading: isLoadingCategoryDetail,
    error: categoryDetailError,
  } = useGetByIdCategoryQuery(
    {
      id: editingCategory?.id || '',
      params: {
        include: 'categoryBrands.category|productDimensions|sizeSpecs|categoryUnits.unit|categoryAttributes.attribute',
      },
    },

    {
      skip: !editMode || !editingCategory?.id || !isOpen,
    },
  );

  const categoryDetail = categoryDetailData?.data;

  const debouncedSearchUpdate = useDebouncedCallback((value: string) => {
    setFilter((prev) => ({
      ...prev,
      attributeSearch: value,
    }));

    setShouldLoadAttributes(true);
  }, 300);
  const handleGenerateCode = useCallback(async () => {
    if (onGenerate) {
      const code = await onGenerate();
      form.setFieldsValue({ code });
      return code;
    }
    return '';
  }, [onGenerate, form]);
  useEffect(() => {
    if (shouldLoadAttributes) {
      const filterConditions = [];

      if (filter.attributeSearch) {
        filterConditions.push(`name:like:%${filter.attributeSearch}%`);
      }
      const queryFilter =
        filterConditions.length > 0 ? filterConditions.map((condition) => `${condition}`).join('&&') : '';

      setAttributeQuery(
        (prev) =>
          ({
            ...prev,
            page: 1,
            filter: queryFilter || undefined,
          }) as unknown as QueryParams,
      );
    }
  }, [filter.attributeSearch, shouldLoadAttributes]);

  const { data: attributesData, isLoading: isAttributesLoading } = useGetAllAttributesQuery(
    { query: attributeQuery },
    { skip: !shouldLoadAttributes },
  );
  useEffect(() => {
    if (isOpen && (isCopyMode || !editMode) && !isCodeGenerated && onGenerate) {
      console.log('Generating code...');
      onGenerate().then((newCode) => {
        setGenCode(newCode);
        form.setFieldsValue({ code: newCode });
        setIsCodeGenerated(true);
      });
    }
    if (!isOpen) {
      setIsCodeGenerated(false);
    }
  }, [isOpen, isCopyMode, isCodeGenerated, onGenerate]);
  useEffect(() => {
    if (editingCategory?.id) {
      setSelectedValues([]);
      setIsActive(true);
    }
  }, [editingCategory?.id]);
  useEffect(() => {
    if (isOpen && editMode && categoryDetail) {
      setTimeout(() => {
        form.resetFields();

        const defaultValues = {
          name: '',
          code: isCopyMode ? genCode : categoryDetail.code || '',
          description: null,
          productDimensions: [],
          sizeSpecs: [],
          categoryAttributes: [],
          categoryUnits: [],
          saleDimensionsData: JSON.stringify({ rowValues: {}, confirmedRows: [] }),
        };
        const currentCode = isCopyMode ? genCode : categoryDetail.code;

        form.setFieldsValue({
          ...defaultValues,
          name: isCopyMode ? `${categoryDetail.name} (Copy)` : categoryDetail.name,
          code: currentCode,
          description: categoryDetail.description,
          productDimensions: categoryDetail.productDimensions?.map((dim) => dim.name) || [],
          sizeSpecs: categoryDetail.sizeSpecs?.map((size) => size.name) || [],
          categoryAttributes: categoryDetail.categoryAttributes?.map((attr) => attr.attributeId) || [],
          categoryUnits:
            categoryDetail.categoryUnits?.map((unit) => ({
              checked: true,
              name: unit?.unit?.name,
              value: unit.convertValue,
              unit: 'Kg',
              unitId: unit.unitId,
            })) || [],
        });

        setIsActive(categoryDetail.isActive || false);
        setSelectedValues(categoryDetail.categoryAttributes?.map((attr) => attr.attributeId) || []);

        if (categoryDetail.saleDimensions) {
          const dimensions = categoryDetail.saleDimensions.split('|');

          if (dimensions.length > 0) {
            const rowValues: Record<number, string[]> = {};
            rowValues[0] = [dimensions[0] || '', dimensions[1] || '', dimensions[2] || '', dimensions[3] || ''];

            if (setExternalRowValues) {
              setExternalRowValues(rowValues);
            }
            if (setExternalConfirmedRows) {
              setExternalConfirmedRows([0]);
            }

            form.setFieldValue(
              'saleDimensionsData',
              JSON.stringify({
                rowValues,
                confirmedRows: [0],
              }),
            );
          } else {
            if (setExternalRowValues) {
              setExternalRowValues({});
            }
            if (setExternalConfirmedRows) {
              setExternalConfirmedRows([]);
            }
          }
        } else {
          if (setExternalRowValues) {
            setExternalRowValues({});
          }
          if (setExternalConfirmedRows) {
            setExternalConfirmedRows([]);
          }
        }
      }, 0);
    }
  }, [isOpen, editMode, categoryDetail, form, setExternalRowValues, setExternalConfirmedRows]);
  const attributes = attributesData?.data?.rows || [];

  const attributeOptions = attributes.map((attr) => ({
    label: attr.name,
    value: attr.id,
    type: attr.type,
    id: attr.id,
  }));

  const isSubmitting = isCreating || isUpdating || isCheckingUnique;

  const handleSubmit = useCallback(
    async (values: CategoryFormValues): Promise<void> => {
      try {
        let formattedSaleDimensions = '';
        let saleDimensionsData = values.saleDimensionsData;
        if (typeof saleDimensionsData === 'string') {
          try {
            saleDimensionsData = JSON.parse(saleDimensionsData);
          } catch (err) {
            console.error('Error parsing saleDimensionsData:', err);
          }
        }
        if (saleDimensionsData) {
          const { rowValues } = saleDimensionsData;

          formattedSaleDimensions = rowValues[0] && rowValues[0]?.length > 0 ? rowValues[0].join('|') : '';
        }

        const formattedProductDimensions = (values.productDimensions || []).map((name) => {
          const existingDimension =
            editMode && editingCategory?.productDimensions
              ? editingCategory.productDimensions.find(
                  (dim: { name: string }) => dim.name.toLowerCase() === name.toLowerCase(),
                )
              : undefined;

          return {
            ...(existingDimension?.id ? { id: existingDimension.id } : {}),
            name,
            isActive: true,
          };
        });

        const formattedSizeSpecs = (values.sizeSpecs || []).map((name) => {
          const existingSize =
            editMode && editingCategory?.sizeSpecs
              ? editingCategory.sizeSpecs.find(
                  (size: { name: string }) => size.name.toLowerCase() === name.toLowerCase(),
                )
              : undefined;

          return {
            ...(existingSize?.id ? { id: existingSize.id } : {}),
            name,
            isActive: true,
          };
        });

        const formattedCategoryUnits = (values.categoryUnits || [])
          .filter((unit) => unit.checked)
          .map((unit) => {
            const existingUnit =
              editMode && editingCategory?.categoryUnits
                ? editingCategory.categoryUnits.find((eu: { unitId: string }) => eu.unitId === unit.unitId)
                : undefined;
            if (!unit.name || !unit.value) return existingUnit || null;
            return {
              ...(existingUnit?.id ? { id: existingUnit.id } : {}),
              name: unit.name || '',
              convertValue: typeof unit.value === 'string' ? parseFloat(unit.value) || 0 : (unit.value as number) || 0,
            };
          })
          .filter(Boolean);

        const formattedCategoryAttributes = (values.categoryAttributes || []).map((attributeId) => {
          const existingAttribute =
            editMode && editingCategory?.categoryAttributes
              ? editingCategory.categoryAttributes.find(
                  (attr: { attributeId: string }) => attr.attributeId === attributeId,
                )
              : undefined;

          return {
            ...(existingAttribute?.id ? { id: existingAttribute.id } : {}),
            attributeId,
          };
        });

        const payload: CreateCategoryRequest = {
          name: values.name,
          code: values.code,
          isActive,
          saleDimensions: formattedSaleDimensions === '|||' ? '' : formattedSaleDimensions,
          productDimensions: formattedProductDimensions,
          sizeSpecs: formattedSizeSpecs,
          categoryUnits: formattedCategoryUnits || [],
          categoryAttributes: formattedCategoryAttributes,
        };

        if (editMode && !isCopyMode && categoryDetail?.id) {
          payload.id = categoryDetail.id;
        }

        if (editMode && !isCopyMode && payload.id) {
          await updateCategory(payload).unwrap();

          showAlertNotification({
            type: 'success',
            title: 'Thao tác thành công',
            message: `Cập nhật danh mục "${values.name}" thành công`,
          });
        } else {
          checkUnique({
            table: TableEnum.Categories,
            field: 'code',
            value: payload.code,
          }).then((response) => {
            if (response?.data?.data.exists) {
              form.setFields([
                {
                  name: 'code',
                  errors: [`Mã danh mục "${payload.code}" đã tồn tại. Vui lòng sử dụng mã khác.`],
                },
              ]);
              showAlertNotification({
                type: 'error',
                title: 'Lỗi',
                message: `Mã danh mục "${payload.code}" đã tồn tại. Vui lòng sử dụng mã khác.`,
              });
              return;
            }
          });
          await createCategory(payload).unwrap();
          showAlertNotification({
            type: 'success',
            title: 'Thao tác thành công',
            message: `Thêm mới danh mục "${values.name}" thành công`,
          });
        }

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error submitting category data:', error);
      }
    },
    [createCategory, updateCategory, onSuccess, isActive, editMode, categoryDetail, editingCategory],
  );

  const handleAttributeSearch = useCallback(
    (value: string) => {
      debouncedSearchUpdate(value);
    },
    [debouncedSearchUpdate],
  );

  const handleAttributeTypeChange = useCallback((types: string[]) => {
    setFilter((prev) => ({
      ...prev,
      attributeType: types,
    }));
    setShouldLoadAttributes(true);
  }, []);

  const handleResetFilter = useCallback(() => {
    setFilter({});
    setShouldLoadAttributes(true);
  }, []);

  const handleToggleActive = useCallback((checked: boolean) => {
    setIsActive(checked);
  }, []);
  return {
    isActive,
    genCode,
    selectedValues,
    categoryDetail,
    isAttributesLoading,
    isSubmitting,
    attributeOptions,
    filter,
    shouldLoadAttributes,
    isLoadingCategoryDetail,
    categoryDetailError,
    setShouldLoadAttributes,
    setSelectedValues,
    handleSubmit,
    handleGenerateCode,
    handleToggleActive,
    handleAttributeSearch,
    handleAttributeTypeChange,
    handleResetFilter,
  };
};
